import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DiaryEntryBody, DiaryFilters, LessonNoteLesson, lessonNotesApi } from '@/services';

export const lessonNotesKeys = {
  all: ['admin', 'lesson-notes'] as const,
  lesson: (l: LessonNoteLesson) => ['admin', 'lesson-notes', 'lesson', l.classId, l.periodId, l.date] as const,
  diary: (f: DiaryFilters) => ['admin', 'lesson-notes', 'diary', f] as const,
  options: () => ['admin', 'lesson-notes', 'options'] as const,
};

export function useLessonNoteQuery(lesson: LessonNoteLesson) {
  return useQuery({ queryKey: lessonNotesKeys.lesson(lesson), queryFn: () => lessonNotesApi.forLesson(lesson) });
}

export function useSaveLessonNoteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: LessonNoteLesson & { body: string }) => lessonNotesApi.saveForLesson(data),
    // Any lesson's "last time" may now point at this entry, so the whole family goes stale.
    onSuccess: () => qc.invalidateQueries({ queryKey: lessonNotesKeys.all }),
  });
}

export function useDiaryQuery(filters: DiaryFilters) {
  return useQuery({
    queryKey: lessonNotesKeys.diary(filters),
    queryFn: () => lessonNotesApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

export function useLessonNoteOptionsQuery() {
  return useQuery({ queryKey: lessonNotesKeys.options(), queryFn: () => lessonNotesApi.options() });
}

export function useCreateDiaryEntryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DiaryEntryBody) => lessonNotesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: lessonNotesKeys.all }),
  });
}

export function useUpdateDiaryEntryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DiaryEntryBody> }) => lessonNotesApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: lessonNotesKeys.all }),
  });
}

export function useDeleteDiaryEntryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => lessonNotesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: lessonNotesKeys.all }),
  });
}
